// Pure maths for drag-to-reorder along one axis (a vertical rail, a horizontal row, or a list of mixed-size rows).
// Every item is described by where it sits along the axis when the drag starts; the dragged item follows the pointer
// on that axis only, and its neighbours slide out of its way. Rows may differ in size (a folder block is taller than
// a channel row), so nothing here assumes a fixed pitch.

/** An item's position along the drag axis: where it starts, and how long it is. */
export type Rect = { start: number; size: number };

const bottom = (r: Rect) => r.start + r.size;
const centre = (r: Rect) => r.start + r.size / 2;

/** Moves the item at `from` so it ends up at index `to`. Returns a new array. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || from >= next.length) return next;
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item);
  return next;
}

/** Keeps the dragged item inside the list: it can't be pulled past the first or last item. */
export function clampDrag(delta: number, s: number, rects: readonly Rect[]): number {
  if (rects.length === 0) return 0;
  return Math.max(rects[0].start - rects[s].start, Math.min(bottom(rects[rects.length - 1]) - bottom(rects[s]), delta));
}

/** The slot the dragged item is heading for: it takes a neighbour's place once its leading edge passes that neighbour's middle. */
export function hoverIndexFor(delta: number, s: number, rects: readonly Rect[]): number {
  let h = s;
  if (delta >= 0) {
    while (h + 1 < rects.length && bottom(rects[s]) + delta > centre(rects[h + 1])) h++;
  } else {
    while (h - 1 >= 0 && rects[s].start + delta < centre(rects[h - 1])) h--;
  }
  return h;
}

/** How far a non-dragged item at `i` slides to open a gap at the hover slot. */
export function shiftFor(i: number, s: number, hover: number, rects: readonly Rect[]): number {
  if (hover > s && i > s && i <= hover) return rects[s].start - rects[s + 1].start;
  if (hover < s && i >= hover && i < s) return bottom(rects[s]) - bottom(rects[s - 1]);
  return 0;
}

/** Where the dragged item comes to rest when released over `hover`, relative to where it started. */
export function settleOffset(s: number, hover: number, rects: readonly Rect[]): number {
  if (hover > s) return bottom(rects[hover]) - bottom(rects[s]);
  if (hover < s) return rects[hover].start - rects[s].start;
  return 0;
}

/**
 * Applies a reorder of the visible ids to the saved order. Ids that aren't visible (hidden spaces, DMs) keep their
 * relative order after the visible ones; the rail sorts unknown ids last, so nothing is lost.
 */
export function mergeOrder(saved: readonly string[], visibleReordered: readonly string[]): string[] {
  const seen = new Set(visibleReordered);
  return [...visibleReordered, ...saved.filter((id) => !seen.has(id))];
}
